import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, CheckCircle2, XCircle, Search, GraduationCap } from 'lucide-react';
import { checkSchoolCodeExists, searchEtablissement, getAcademies, type EtablissementSearchResult } from '@/api/schools';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';
import type { AddressSuggestion } from '@/api/schools';
import { getCountryOptions, countryCodeToFlag, getCountryByCode } from '@/constants/countries';
import type { WizardFormData } from '../types';
import type { SchoolOptions, Academie } from '@/types/school';

const { priority: PRIORITY_COUNTRIES, others: OTHER_COUNTRIES } = getCountryOptions();

interface InfoStepProps {
  formData: WizardFormData;
  onChange: (data: Partial<WizardFormData>) => void;
  options: SchoolOptions | null;
  errors?: Record<string, string>;
  onCodeValidation?: (isValid: boolean, error?: string) => void;
}

export function InfoStep({ formData, onChange, options, errors, onCodeValidation }: InfoStepProps) {
  const [checkingCode, setCheckingCode] = useState(false);
  const [codeExists, setCodeExists] = useState<boolean | null>(null);
  const [academies, setAcademies] = useState<Academie[]>([]);
  const [loadingAcademies, setLoadingAcademies] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<EtablissementSearchResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Load academies on mount
  useEffect(() => {
    const loadAcademies = async () => {
      setLoadingAcademies(true);
      try {
        const data = await getAcademies();
        setAcademies(data);
      } catch (err) {
        console.error('Error loading academies:', err);
      } finally {
        setLoadingAcademies(false);
      }
    };
    loadAcademies();
  }, []);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced autocomplete search
  useEffect(() => {
    if (searchQuery.length < 3) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await searchEtablissement(searchQuery);
        setSearchResults(response.results);
        setShowResults(true);
      } catch (err) {
        console.error('Error searching:', err);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSelectResult = (result: EtablissementSearchResult) => {
    const updates: Partial<WizardFormData> = {};

    // Always update RNE and academie
    updates.rne = result.rne || '';
    updates.codeAcademie = result.codeAcademie || '';

    // Update name and generate code
    if (result.name) {
      updates.name = result.name;
      updates.code = generateCode(result.name);
    }

    // Update address
    if (result.address || result.postalCode || result.city) {
      const fullAddress = [result.address, result.postalCode, result.city].filter(Boolean).join(' ');
      updates.address = fullAddress;
    }

    // Update contact info
    if (result.email) {
      updates.email = result.email;
    }
    if (result.phone) {
      updates.phone = result.phone;
    }

    // Store coordinates from EN API
    if (result.latitude && result.longitude) {
      updates.latitude = result.latitude;
      updates.longitude = result.longitude;
    }

    onChange(updates);
    setShowResults(false);
    setSearchQuery('');
  };

  const generateCode = (name: string): string => {
    // Generate a trigram from the name (first letter of each significant word)
    const normalized = name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .toUpperCase();

    // Filter out common words and get first letters
    const excludeWords = ['DE', 'DU', 'DES', 'LA', 'LE', 'LES', 'ET', 'L', 'D', 'A', 'AU', 'AUX'];
    const words = normalized.split(/[^A-Z0-9]+/).filter(w => w && !excludeWords.includes(w));

    if (words.length >= 3) {
      // Take first letter of first 3 significant words
      return words.slice(0, 3).map(w => w[0]).join('');
    } else if (words.length === 2) {
      // Take first letter of each word + second letter of longest word
      const sorted = [...words].sort((a, b) => b.length - a.length);
      return words[0][0] + words[1][0] + (sorted[0].length > 1 ? sorted[0][1] : '');
    } else if (words.length === 1 && words[0].length >= 3) {
      // Take first 3 letters
      return words[0].substring(0, 3);
    }

    return normalized.replace(/[^A-Z0-9]/g, '').substring(0, 3);
  };

  // Debounced code check
  useEffect(() => {
    if (!formData.code || formData.code.length < 2) {
      setCodeExists(null);
      return;
    }

    // Validate format first
    if (!/^[A-Z0-9]+$/.test(formData.code)) {
      setCodeExists(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setCheckingCode(true);
      try {
        const exists = await checkSchoolCodeExists(formData.code);
        setCodeExists(exists);
        if (onCodeValidation) {
          onCodeValidation(!exists, exists ? `Le code "${formData.code}" est déjà utilisé par une autre école` : undefined);
        }
      } catch (err) {
        console.error('Error checking code:', err);
        setCodeExists(null);
      } finally {
        setCheckingCode(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.code, onCodeValidation]);

  const handleNameChange = (name: string) => {
    const previousCode = generateCode(formData.name);
    onChange({ name });
    // Auto-generate code if not manually edited
    if (!formData.code || formData.code === previousCode) {
      onChange({ code: generateCode(name) });
    }
  };

  const handleCodeChange = (code: string) => {
    setCodeExists(null); // Reset while typing
    onChange({ code: code.toUpperCase().replace(/[^A-Z0-9]/g, '') });
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-xl font-semibold text-foreground">
          Informations de base
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Renseignez les informations principales de l'école
        </p>
      </div>

      <div className="grid gap-6">
        {/* Education Nationale Toggle */}
        <div className="p-4 border rounded-lg bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <GraduationCap className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <Label htmlFor="isEducationNationale" className="text-base font-medium cursor-pointer">
                  Etablissement Education Nationale
                </Label>
                <p className="text-sm text-muted-foreground">
                  École reconnue par l'Éducation Nationale française
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <button
                type="button"
                className={`w-12 py-1 text-sm font-medium rounded-md transition-colors text-center ${
                  formData.isEducationNationale
                    ? 'bg-white shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => onChange({ isEducationNationale: true })}
              >
                Oui
              </button>
              <button
                type="button"
                className={`w-12 py-1 text-sm font-medium rounded-md transition-colors text-center ${
                  !formData.isEducationNationale
                    ? 'bg-white shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => {
                  onChange({ isEducationNationale: false, rne: '', codeAcademie: '' });
                }}
              >
                Non
              </button>
            </div>
          </div>
        </div>

        {/* RNE and Academie Section - Only shown if Education Nationale */}
        {formData.isEducationNationale && (
          <div className="pb-4 border-b">
            <h3 className="text-sm font-medium text-muted-foreground mb-4">
              Recherche dans l'annuaire Education Nationale
            </h3>

            {/* Search by RNE or name */}
            <div className="space-y-4">
              <div className="space-y-2 relative z-10" ref={searchRef}>
                <Label htmlFor="etablissement-search">Rechercher un etablissement</Label>
                <div className="relative">
                  <Input
                    id="etablissement-search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchResults.length > 0 && setShowResults(true)}
                    placeholder="Code RNE ou nom de l'etablissement..."
                    className="pr-10"
                  />
                  {searching && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                  {!searching && searchQuery.length >= 3 && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Search className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  {showResults && searchResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                      {searchResults.map((result, index) => (
                        <button
                          key={result.rne || index}
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-muted border-b last:border-b-0"
                          onClick={() => handleSelectResult(result)}
                        >
                          <div className="font-medium text-sm">{result.name || 'Sans nom'}</div>
                          <div className="text-xs text-muted-foreground">
                            {result.rne && <span className="font-mono">{result.rne}</span>}
                            {result.city && <span> - {result.city}</span>}
                            {result.postalCode && <span> ({result.postalCode})</span>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {showResults && searchResults.length === 0 && !searching && searchQuery.length >= 3 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg p-3 text-sm text-muted-foreground">
                      Aucun resultat trouve
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Tapez au moins 3 caracteres pour rechercher par code RNE ou nom
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rne">Code RNE (UAI)</Label>
                  <Input
                    id="rne"
                    value={formData.rne}
                    onChange={(e) => onChange({ rne: e.target.value.toUpperCase() })}
                    placeholder="Ex: 0750001A"
                  />
                  <p className="text-xs text-muted-foreground">
                    Rempli automatiquement lors de la selection
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="codeAcademie">Academie</Label>
                  <Select
                    value={formData.codeAcademie || 'none'}
                    onValueChange={(value) =>
                      onChange({ codeAcademie: value === 'none' ? '' : value })
                    }
                    disabled={loadingAcademies}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingAcademies ? 'Chargement...' : 'Selectionner une academie'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      {academies.map((academie) => (
                        <SelectItem key={academie.code} value={academie.code}>
                          {academie.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Rempli automatiquement lors de la selection
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Name and Code */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              Nom de l'école <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Ex: École Saint-Joseph"
              className={errors?.name ? 'border-red-500' : ''}
            />
            {errors?.name && (
              <p className="text-xs text-red-500">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="code">
              Code <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => handleCodeChange(e.target.value)}
                placeholder="Ex: ESJ"
                className={errors?.code || codeExists ? 'border-red-500 pr-10' : codeExists === false ? 'border-green-500 pr-10' : ''}
              />
              {checkingCode && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
              {!checkingCode && codeExists === false && formData.code && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                </div>
              )}
              {!checkingCode && codeExists === true && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <XCircle className="h-4 w-4 text-red-500" />
                </div>
              )}
            </div>
            {errors?.code && (
              <p className="text-xs text-red-500">{errors.code}</p>
            )}
            {!errors?.code && codeExists === true && (
              <p className="text-xs text-red-500">
                Ce code est déjà utilisé par une autre école. Veuillez en choisir un autre.
              </p>
            )}
            {!errors?.code && codeExists === false && (
              <p className="text-xs text-green-600">
                Ce code est disponible
              </p>
            )}
            {codeExists === null && !errors?.code && (
              <p className="text-xs text-muted-foreground">
                Trigramme unique de l'école (généré automatiquement)
              </p>
            )}
          </div>
        </div>

        {/* Email and Phone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email de contact</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => onChange({ email: e.target.value })}
              placeholder="contact@ecole.fr"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Telephone</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => onChange({ phone: e.target.value })}
              placeholder="01 23 45 67 89"
            />
          </div>
        </div>

        {/* Email Sender */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="emailSender">
              Email expediteur <span className="text-red-500">*</span>
            </Label>
            <Input
              id="emailSender"
              type="email"
              value={formData.emailSender}
              onChange={(e) => onChange({ emailSender: e.target.value })}
              placeholder="noreply@ecole.fr"
              className={errors?.emailSender ? 'border-red-500' : ''}
            />
            {errors?.emailSender && (
              <p className="text-xs text-red-500">{errors.emailSender}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Adresse utilisee pour l'envoi des emails
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="domainSender">Domaine expediteur</Label>
            <Input
              id="domainSender"
              value={formData.domainSender}
              onChange={(e) => onChange({ domainSender: e.target.value })}
              placeholder="ecole.fr"
            />
          </div>
        </div>

        {/* Country */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="country">Pays</Label>
            <Select
              value={formData.country || 'FR'}
              onValueChange={(value) => onChange({ country: value, latitude: null, longitude: null })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un pays" />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {countryCodeToFlag(c.code)} {c.label}
                  </SelectItem>
                ))}
                <div className="border-t my-1" />
                {OTHER_COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {countryCodeToFlag(c.code)} {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Addresses */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="address">Adresse</Label>
            <AddressAutocomplete
              id="address"
              value={formData.address}
              country={formData.country || 'FR'}
              onChange={(address) => onChange({ address })}
              onSelect={(suggestion: AddressSuggestion) => {
                onChange({
                  address: suggestion.label,
                  latitude: suggestion.latitude,
                  longitude: suggestion.longitude,
                });
              }}
              placeholder="12 rue de l'École, 75001 Paris"
            />
            <Input
              id="addressComplement"
              value={formData.addressComplement}
              onChange={(e) => onChange({ addressComplement: e.target.value })}
              placeholder="Complément d'adresse"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="addressHeadoffice">Siège social</Label>
            <AddressAutocomplete
              id="addressHeadoffice"
              value={formData.addressHeadoffice}
              country={formData.country || 'FR'}
              onChange={(addressHeadoffice) => onChange({ addressHeadoffice })}
              onSelect={(suggestion: AddressSuggestion) => {
                onChange({ addressHeadoffice: suggestion.label });
              }}
              placeholder="Adresse du siège social"
            />
            <Input
              id="addressHeadofficeComplement"
              value={formData.addressHeadofficeComplement}
              onChange={(e) => onChange({ addressHeadofficeComplement: e.target.value })}
              placeholder="Complément d'adresse"
            />
          </div>
        </div>

        {/* Network */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="network">Reseau</Label>
            <Select
              value={formData.networkId?.toString() || 'none'}
              onValueChange={(value) =>
                onChange({ networkId: value === 'none' ? null : parseInt(value) })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selectionner un reseau" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun</SelectItem>
                {options?.networks.map((network) => (
                  <SelectItem key={network.id} value={network.id.toString()}>
                    {network.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

      </div>
    </div>
  );
}
